'use client';

import { useSessionStore } from '@/app/store/use-session-store';
import {
  PointStatus,
  useAddressStore,
} from '@/entities/address/model/use-address-store';
import { useRouter } from '@/i18n/navigation';
import ConfirmModal from '@/widgets/confirm-modal';
import UniversalHeader from '@/widgets/header';
import { Box, Button, Flex, Stack, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { RefObject, useState } from 'react';
import AddressDetails from '../components/AddressDetails';
import YandexMap from '../components/YandexMap';
import AddressSearch from '../components/YandexMap/components/AddressSearch';
import TrashRed from '../icons/TrashRed';

const Mobile = ({
  onClose,
  city,
  mapRef,
  onChange,
  geometry,
  isLoading,
  addressName,
  markerCoords,
  accommodation,
  handleClear,
  onMapBounded,
  onAddressSelect,
}: {
  onClose: () => void;
  mapRef: RefObject<null>;
  geometry: [number, number];
  city: string;
  isLoading: boolean;
  addressName: string;
  handleClear: () => void;
  markerCoords: [number, number];
  accommodation: string;
  onAddressSelect: (e: any) => void;
  onMapBounded: (e: number[]) => void;
  onChange: (
    value: { address: string; city: string; house: string; location: number[] },
    isSelect: boolean
  ) => void;
}) => {
  const [isDialog, setDialog] = useState(false);
  const [details, setDetails] = useState<boolean>(false);

  const router = useRouter();
  const t = useTranslations();

  const editingPoint = useAddressStore((state) => state.editing_point);
  const removePoint = useAddressStore((state) => state.removePoint);
  const resetGeoL = useSessionStore((state) => state.resetGeoL);

  const handleAddress = () => {
    if (addressName !== '' && city !== null) {
      setDetails(true);
    }
  };

  const onClear = () => {
    setDetails(false);
    handleClear();
  };

  return (
    <>
      <Flex
        flex="auto"
        position="relative"
        flexDirection="column"
        maxH="100dvh"
        overflow="hidden" /* Prevent overflow issues */
      >
        {/* Top Header - Fixed Height */}
        <Flex
          flexDir="column"
          bg="background.card"
          color={'text'}
          flexShrink={0}
          position="relative"
          zIndex={1}
        >
          <UniversalHeader
            onBack={() => {
              if (editingPoint || !details) {
                // setAddressName('')
                onClose();
              }
              setDetails(false);
            }}
            title={details ? t('address_information') : t('new_address')}
            icon={details && editingPoint?.id ? <TrashRed color="red" /> : null}
            onClick={() => details && setDialog(true)}
          />
          {!details && (
            <Box
              p={4}
              w="100%"
              top="100%"
              left="0px"
              zIndex={1}
              position="absolute"
              bg="background.card"
              borderBottom="1px solid #0000000D"
            >
              <AddressSearch
                region={geometry}
                value={addressName}
                onSelect={onAddressSelect}
              />
            </Box>
          )}
        </Flex>

        {/* Map - Flexible Centered Content */}
        <Flex flex="1" overflow="hidden" pt={4}>
          <YandexMap
            mapRef={mapRef}
            geometry={geometry}
            outerLoading={isLoading}
            onLocationChange={onChange}
            onMapBounded={onMapBounded}
          />
        </Flex>

        {/* Bottom Content - Dynamic Height */}
        <Stack
          p={4}
          gap={4}
          bg="background.card"
          zIndex={1}
          flexShrink={0}
          position="relative"
          borderTop="1px solid #0000000D"
        >
          {details ? (
            <AddressDetails
              city={city}
              onClear={onClear}
              coords={markerCoords}
              address={addressName}
              accommodation={accommodation}
            />
          ) : (
            addressName && (
              <Box
                pt={4}
                px={4}
                position="absolute"
                bottom="100%"
                width="100%"
                left={0}
                bg="background.card"
                color={'text'}
              >
                <Text textAlign="center" fontWeight="bold">
                  {addressName}
                </Text>
              </Box>
            )
          )}
          <Button
            onClick={handleAddress}
            size="xl"
            rounded={16}
            h="56px"
            bg="primary"
            color="primary.text"
            _hover={{
              bg: 'primary.hover',
            }}
            disabled={isLoading || !addressName}
          >
            {isLoading ? t('location.determining_location') : t('continue')}
          </Button>
        </Stack>
      </Flex>

      <ConfirmModal
        isWarning={true}
        isOpen={isDialog}
        onConfirm={() => {
          if (editingPoint?.status === PointStatus.ACTIVE) {
            resetGeoL();
            setTimeout(() => {
              router.refresh();
            }, 1000);
          }
          if (editingPoint?.id) removePoint(editingPoint?.id);
          onClose();
          onClear();
        }}
        title={t('remove_address')}
        onClose={() => setDialog(false)}
        description={''}
      />
    </>
  );
};

export default Mobile;
